import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import WorkshopStepWrapper from '../WorkshopStepWrapper';
import { useWorkshopProgress } from '@/contexts/WorkshopProgressContext';
import { Target } from 'lucide-react';

const originalContent = `Dear Supporter,

Thank you for your donation to our organization. Your support helps us continue our important work in the community. We appreciate your generosity and look forward to updating you on our progress.

Best regards,
The Team`;

export const WorkshopChallenge: React.FC = () => {
  const { navigateToStep } = useWorkshopProgress();

  return (
    <WorkshopStepWrapper
      step="challenge"
      title="The Challenge: Fix This AI Output"
      description="Help me transform generic AI into professional content"
      showSkillsPreview={true}
      autoMarkComplete={true}
    >
      {/* Lyra's Challenge Introduction */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
        <div className="flex items-start space-x-6">
          <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
            <SupabaseIcon
              iconPath="lyra-avatar.png"
              alt="Lyra"
              size="lg"
              className="w-12 h-12"
            />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Lyra's Live Coaching</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              "I need your help! I asked AI to write a donor thank you email, but look what I got back.
              This is classic generic AI output - it sounds like it could be from any organization anywhere.
              Let's fix this together using professional AI direction techniques."
            </p>
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl">
              <div className="text-purple-800 font-semibold">Your Mission:</div>
              <div className="text-purple-700 mt-2">Transform this generic output into mission-aligned, impactful communication</div>
            </div>
          </div>
        </div>
      </div>

      {/* The Problematic AI Output */}
      <Card className="mb-8 border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-700 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            The Problem: Generic AI Output
          </CardTitle>
          <CardDescription>This is what AI gave me when I asked for a "donor thank you email"</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-white p-6 rounded-xl border text-gray-700 leading-relaxed">
            {originalContent}
          </div>
          <div className="mt-4 p-4 bg-red-100 rounded-xl">
            <h4 className="font-semibold text-red-800 mb-2">What's Wrong Here?</h4>
            <div className="space-y-2 text-red-700 text-sm">
              <div>❌ No specific donor name or details</div>
              <div>❌ No concrete impact or outcomes</div>
              <div>❌ Generic "organization" language</div>
              <div>❌ Corporate tone instead of personal</div>
              <div>❌ No call to action or engagement</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lyra's Guidance */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
            <SupabaseIcon
              iconPath="lyra-avatar.png"
              alt="Lyra"
              size="md"
              className="w-8 h-8"
            />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 mb-2">Lyra's Coaching Insight</h4>
            <p className="text-gray-700 mb-4">
              "This happens because I gave AI a lazy prompt: 'Write a donor thank you email.'
              Professional AI direction requires specifics: WHO the donor is, WHAT they funded,
              HOW it created impact, and WHO I am. Ready to practice the professional approach?"
            </p>
            <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
              <strong className="text-purple-800">Your Challenge:</strong>
              <div className="text-purple-700 mt-2">Help me rewrite this using specific context, impact details, and authentic voice</div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <Button
          onClick={() => navigateToStep('practice')}
          size="lg"
          className="bg-purple-600 hover:bg-purple-700"
        >
          Let's Fix This Together →
        </Button>
      </div>
    </WorkshopStepWrapper>
  );
};

export default WorkshopChallenge;